import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";
import { fmtCAD } from "@/lib/api";
import { toast } from "sonner";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const { t } = useI18n();
  const onSale = product.compare_at && product.compare_at > product.price;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (index % 8) * 0.05 }}
      className="group border border-gray-200 bg-white flex flex-col"
      data-testid={`product-card-${product.id}`}>
      <Link to={`/produit/${product.handle}`} className="relative block overflow-hidden bg-gray-50 aspect-square">
        {onSale && (
          <span className="absolute top-3 left-3 z-10 bg-brand-red text-white text-[11px] font-bold px-2 py-1">{t("product.sale")}</span>
        )}
        {product.used && (
          <span className="absolute top-3 left-3 z-10 bg-brand-navy text-white text-[11px] font-bold px-2 py-1" data-testid={`used-badge-${product.id}`}>{t("used.badge")}</span>
        )}
        {product.used && product.status === "sold" && (
          <span className="absolute top-3 right-3 z-10 bg-black text-white text-[11px] font-bold px-2 py-1" data-testid={`sold-badge-${product.id}`}>{t("used.sold")}</span>
        )}
        {!product.in_stock && (
          <span className="absolute top-3 right-3 z-10 bg-black/80 text-white text-[11px] px-2 py-1">{t("product.out")}</span>
        )}
        {product.image ? (
          <img src={product.image} alt={product.title}
            className="h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
            loading="lazy" />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-200 text-center px-4 gap-3"
            data-testid={`product-image-fallback-${product.id}`}>
            <img src="/logo.png" alt="DCS Électro" className="h-9 w-auto opacity-90" loading="lazy" />
            <span className="font-heading text-base font-bold text-brand-navy leading-tight">{product.brand}</span>
            <span className="text-[11px] uppercase tracking-wider text-gray-500">{product.category_label}</span>
          </div>
        )}
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <span className="text-[11px] uppercase tracking-widest text-brand-red font-semibold">{product.brand}</span>
        <Link to={`/produit/${product.handle}`} className="mt-1 text-sm font-medium text-gray-900 line-clamp-2 hover:text-brand-red min-h-[2.5rem]">
          {product.title}
        </Link>
        <div className="mt-3 flex items-center gap-2 min-h-[1.75rem]">
          {product.price_on_request ? (
            <span className="text-base font-bold text-brand-navy">{t("product.onRequest")}</span>
          ) : (
            <>
              <span className="text-lg font-bold text-gray-900">{fmtCAD(product.price)}</span>
              {onSale && <span className="text-sm text-gray-400 line-through">{fmtCAD(product.compare_at)}</span>}
            </>
          )}
        </div>
        {product.price_on_request ? (
          <Link to="/contact"
            className="mt-4 flex items-center justify-center gap-2 bg-brand-navy text-white text-sm font-semibold py-2.5 hover:bg-black transition-colors"
            data-testid={`ask-price-${product.id}`}>
            {t("product.askPrice")}
          </Link>
        ) : product.used && product.status === "sold" ? (
          <div className="mt-4 flex items-center justify-center gap-2 bg-gray-200 text-gray-500 text-sm font-semibold py-2.5 cursor-not-allowed" data-testid={`sold-label-${product.id}`}>
            {t("used.sold")}
          </div>
        ) : (
          <button
            onClick={() => { addItem(product); toast.success(t("product.added"), { description: product.title }); }}
            className="mt-4 flex items-center justify-center gap-2 bg-brand-red text-white text-sm font-semibold py-2.5 hover:bg-[#b81c14] transition-colors"
            data-testid={`add-to-cart-${product.id}`}>
            <ShoppingBag size={15} /> {t("product.add")}
          </button>
        )}
      </div>
    </motion.div>
  );
};
